import { useEffect, useState } from 'react';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Building2, Sparkles, ArrowRight } from 'lucide-react';
import { CreateWorkspaceModal } from './CreateWorkspaceModal';

const STORAGE_KEY = 'dsos_pro_unlock_shown';

export function ProUnlockModal() {
  const { plan, workspaceLimit, loading } = useEntitlements();
  const { workspaces } = useWorkspaceScope();
  const [open, setOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  useEffect(() => {
    if (loading) return;

    // Check if user is Pro (limit = 3) and hasn't seen the modal
    const isPro = plan === 'pro';
    const hasSeenModal = localStorage.getItem(STORAGE_KEY) === 'true';
    const currentCount = workspaces.length;

    // Show modal if:
    // 1. User is Pro (workspaceLimit = 3)
    // 2. Hasn't seen the modal yet
    // 3. Has less than 3 workspaces (can still create more)
    if (isPro && !hasSeenModal && currentCount < workspaceLimit) {
      setOpen(true);
      localStorage.setItem(STORAGE_KEY, 'true');
    }
  }, [plan, workspaceLimit, loading, workspaces.length]);

  const handleCreateWorkspace = () => {
    setOpen(false);
    setCreateModalOpen(true);
  };

  const handleDismiss = () => {
    setOpen(false);
  };

  if (!open) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-xl">Pro Unlocked!</DialogTitle>
                <DialogDescription className="mt-1">
                  You can now create up to {workspaceLimit} workspaces
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              With Pro, you can organize your leads and deals across multiple workspaces. 
              Create and manage up to {workspaceLimit} workspaces.
            </p>

            <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
              <Building2 className="w-5 h-5 text-primary flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium">Create your first workspace</p>
                <p className="text-xs text-muted-foreground">
                  Organize your sales pipeline by business unit, product, or team
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={handleDismiss}>
              Maybe later
            </Button>
            <Button onClick={handleCreateWorkspace}>
              Create Workspace
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <CreateWorkspaceModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
      />
    </>
  );
}

